import React, { useState, useEffect } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { useSelector } from "react-redux";

const MAX_DESCRIPTION_LENGTH = 2000;

const Step2Details = ({ form, setForm, nextStep, prevStep }) => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken; 
  // const token = loggedInUser?.token; 
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // eslint-disable-next-line no-unused-vars
  const [propertyPurposes, setPropertyPurposes] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [propertyTypes, setPropertyTypes] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [propertySubtypes, setPropertySubtypes] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [propertySubSubtypes, setPropertySubSubtypes] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isCityDisabled, setIsCityDisabled] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [selectedPropertyType, setSelectedPropertyType] = useState(form?.propertyType || "");
  // eslint-disable-next-line no-unused-vars
  const [selectedPropertyPurpose, setSelectedPropertyPurpose] = useState(form?.propertyPurpose || "");
  const [selectedState, setSelectedState] = useState(form?.state || "");
  const [characterCount, setCharacterCount] = useState(form?.description?.length || 0);
  const [textData, setTextData] = useState(form?.description || "");
  // eslint-disable-next-line no-unused-vars
  const [defaultStatusId, setDefaultStatusId] = useState(null);

  const postData = {
    page_number: 1,
    page_size: 20,
    order_by: "ASC",
  };

  // Fetch all dropdown data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Property Purposes
        const purposeResponse = await axios.post(
          `${baseUrl}/property/purposes`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (purposeResponse.data.statusType === "SUCCESS") {
          setPropertyPurposes(purposeResponse.data.propertyPurposes);
        } else {
          setPropertyPurposes([]);
        } 

        // Property Types
        const typeResponse = await axios.post(
          `${baseUrl}/property/types`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (typeResponse.data.statusType === "SUCCESS") {
          setPropertyTypes(typeResponse.data.propertyTypes);
        } else {
          setPropertyTypes([]);
        }

        
      } catch (error) {
        setCountries([]);
        setPropertyPurposes([]);
        setPropertyTypes([]);
      }
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, token]);

  // Fetch states/cities
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.post(
          `${baseUrl}/admin/states/1`, 
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.statusType === "SUCCESS") {
          setStates(response.data.states);
          setIsCityDisabled(false);
        } else {
          setStates([]);
          setIsCityDisabled(true);
        }
      } catch (error) {
        setStates([]);
        setIsCityDisabled(true);
      }
    };

    fetchStates(); 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, token]);

  useEffect(() => {
    const fetchCities = async (stateId) => {
      if (!stateId) {
        setCities([]);
        return;
      }
      try {
        const response = await axios.post(
          `${baseUrl}/admin/cities/${stateId}`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.statusType === "SUCCESS") {
          setCities(response.data.cities);
        } else {
          setCities([]);
        }
      } catch (error) {
        setCities([]);
      }
    };

    fetchCities(selectedState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, baseUrl, token]);

  // Fetch subtypes when property type changes
  useEffect(() => {
    const fetchSubtypes = async (propertyTypeId) => {
      if (!propertyTypeId) {
        setPropertySubtypes([]);
        return;
      }
      try {
        const response = await axios.post(
          `${baseUrl}/property/subtypes/${propertyTypeId}`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.statusType === "SUCCESS") {
          setPropertySubtypes(response.data.propertySubtypes);
        } else {
          setPropertySubtypes([]);
        }
      } catch (error) {
        setPropertySubtypes([]);
      }
    };

    fetchSubtypes(selectedPropertyType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyType, baseUrl, token]);

  // Fetch subsubtypes when property subtype changes
  useEffect(() => {
    const fetchSubSubtypes = async (propertySubtypeId) => {
      if (!propertySubtypeId) {
        setPropertySubSubtypes([]);
        return;
      }
      try {
        const response = await axios.post(
          `${baseUrl}/property/subsubtypes/${propertySubtypeId}`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.statusType === "SUCCESS") {
          setPropertySubSubtypes(response.data.propertySubSubtypes);
        } else {
          setPropertySubSubtypes([]);
        }
      } catch (error) {
        setPropertySubSubtypes([]);
      }
    };

    fetchSubSubtypes(form?.propertySubtype);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.propertySubtype, baseUrl, token]);

  const validationSchema = Yup.object({
    price: Yup.string().required("Budget is required"),
    name: Yup.string().required("Title is required"),
    featured_property: Yup.boolean().nullable().required("Please select if this is a new property"),
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    area: Yup.string().required("Area is required"),
    description: Yup.string().max(MAX_DESCRIPTION_LENGTH, "Description too long"),
  });

  const initialValues = {
    price: form?.price || "",
    name: form?.name || "",
    featured_property: typeof form?.featured_property === 'boolean' 
      ? form.featured_property 
      : (form?.featured_property === "Yes" ? true : form?.featured_property === "No" ? false : false),
    state: form?.state || "",
    stateName: form?.stateName || "",
    city: form?.city || "",
    cityName: form?.cityName || "",
    area: form?.area || "",
    description: form?.description || "",
  };

  const handleSubmit = (values, { setSubmitting }) => {
    setForm((prev) => ({
      ...prev,
      ...values,
      description: textData,
    }));
    setSubmitting(false);
    if (nextStep) nextStep();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ isSubmitting, setFieldValue, values }) => (
        <Form className="p-4 bg-light rounded shadow-sm">
          <Row className="mb-4 row-gap-4">
            <Col md={4}>
              <h5 className="mb-2">Budget</h5>
              <Field
                type="text"
                className="form-control bg-white h-10 form-control-sm"
                id="price"
                placeholder="Enter Budget"
                name="price"
                style={{
                  height: "2.3rem",
                  padding: ".375rem 2.25rem .375rem .75rem",
                  backgroundColor: "#fff",
                }}
              />
              <ErrorMessage name="price" component="div" className="text-danger" />
            </Col>
            <Col md={4}>
              <h5 className="mb-2">Title</h5>
              <Field
                type="text"
                className="form-control bg-white h-10 form-control-sm"
                id="name"
                placeholder="Enter Title"
                name="name"
                style={{
                  height: "2.3rem",
                  padding: ".375rem 2.25rem .375rem .75rem",
                  backgroundColor: "#fff",
                }}
              />
              <ErrorMessage name="name" component="div" className="text-danger" />
            </Col>
            <Col md={4}>
              <h5 className="mb-2">New</h5>
              <Field
                as="select"
                className="form-select"
                id="featured_property"
                name="featured_property"
                value={values.featured_property === true ? "Yes" : values.featured_property === false ? "No" : ""}
                onChange={(e) => {
                  setFieldValue("featured_property", e.target.value === "Yes");
                }}
              >
                <option value="" label="Select option" />
                <option value="Yes" label="Yes" />
                <option value="No" label="No" />
              </Field>
              <ErrorMessage name="featured_property" component="div" className="text-danger" />
            </Col>
          </Row>

          <Row className="mb-4 row-gap-4">
            <Col md={4}>
              <h5 className="mb-2">State</h5> 
              <Field
                as="select"
                className="form-select bg-white h-10"
                id="state"
                name="state"
                onChange={(e) => {
                  const selectedOption = e.target.selectedOptions[0];
                  const name = selectedOption.getAttribute("data-name");
                  const stateId = e.target.value;
                  setFieldValue("state", stateId);
                  setFieldValue("stateName", name);
                  setSelectedState(stateId);
                  setFieldValue("city", "");
                }}
                value={values.state}
              >
                <option value="">Select state</option>
                {states.map((state) => (
                  <option key={state.id} value={state.id} data-name={state.name}>
                    {state.name}
                  </option>
                ))}
              </Field>

              <ErrorMessage name="state" component="div" className="text-danger" />
            </Col>

            <Col md={4}>
              <h5 className="mb-2">City</h5>
              <Field
                as="select"
                className="form-select bg-white h-10"
                id="city"
                name="city"
                disabled={isCityDisabled}
                value={values.city}
                onChange={(e) => {
                  const selectedOption = e.target.selectedOptions[0];
                  const name = selectedOption.getAttribute("data-name");
                  setFieldValue("city", e.target.value);
                  setFieldValue("cityName", name);
                }}
              >
                <option value="">Select city</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id} data-name={city.name}>
                    {city.name} 
                  </option>
                ))}
              </Field>
              <ErrorMessage name="city" component="div" className="text-danger" />
            </Col>
            <Col md={4}>
              <h5 className="mb-2">Area</h5>
              <Field
                type="text"
                className="form-control bg-white h-10 form-control-sm"
                id="area"
                placeholder="Enter area"
                name="area"
                value={values.area}
              />
              <ErrorMessage name="area" component="div" className="text-danger" />
            </Col>
            <Col md={4}></Col>
          </Row>

          <div className="mt-4">
            <h5 className="mb-2">Description</h5>
            <ReactQuill
              theme="snow"
              className="bg-white"
              value={textData}
              onChange={(value) => {
                setTextData(value);
                setCharacterCount(value.length);
              }}
            />
            <div className="mt-2 small text-muted">
              <span>
                {characterCount}/{MAX_DESCRIPTION_LENGTH} characters
              </span>
              {characterCount > MAX_DESCRIPTION_LENGTH && (
                <div className="text-danger small">
                  Description exceeds the maximum length.
                </div>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-between mt-4">
            <Button
              variant="secondary"
              type="button"
              onClick={prevStep}
              disabled={isSubmitting}
            >
              Previous
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting || characterCount > MAX_DESCRIPTION_LENGTH}
            >
              Next
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Step2Details;